import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, Search, User, ArrowRight, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { PageHeader, Card, Button, Loading, EmptyState, SeverityTag, TierBadge } from "@/components/kit";
import { memberQrValue } from "@/lib/memberCard";

export default function ScanMember() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "owner" ? "/owner" : "/admin";
  const [scanning, setScanning] = useState(false);
  const [permError, setPermError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    api.ownerMembers().then(setMembers).catch(() => {});
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolve = async (code) => {
    setLoading(true); setResult(null);
    try { setResult(await api.resolveMemberQr(code)); }
    catch (e) { toast.error(e.message === "404" ? "Member QR tidak ditemukan" : (e.message || "QR tidak valid")); }
    finally { setLoading(false); }
  };

  const onScan = async (text) => { await stop(); resolve(text); };

  const start = async () => {
    setPermError(false); setScanning(true);
    try {
      const el = document.getElementById("scan-reader");
      if (!el) return;
      const scanner = new Html5Qrcode("scan-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, onScan, () => {});
    } catch { setScanning(false); setPermError(true); }
  };

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (s) { try { await s.stop(); s.clear(); } catch {} }
  };

  const filtered = q.trim() ? members.filter((m) => (m.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];

  return (
    <div>
      <PageHeader eyebrow="Scan Member" title="Scan Member QR"
        subtitle="Pindai QR kartu member (dari HP member atau file PNG) untuk verifikasi identitas & status membership." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 hsc-label">Kamera</div>
          <div id="scan-reader" className="mx-auto mb-4 w-full max-w-xs overflow-hidden rounded-2xl border border-[#1F1F22] bg-black" />
          {permError && (
            <div className="mb-3 rounded-lg border border-[#3A3A3C] bg-[#160b0b] p-3 text-sm text-[#FF8080]">
              Akses kamera ditolak. Izinkan kamera di pengaturan browser lalu coba lagi, atau gunakan pencarian manual.
            </div>
          )}
          {!scanning ? (
            <Button className="w-full" onClick={start} data-testid="start-scan-member"><Camera className="h-4 w-4" /> Mulai Scan</Button>
          ) : (
            <Button variant="outline" className="w-full" onClick={stop} data-testid="stop-scan-member"><CameraOff className="h-4 w-4" /> Stop</Button>
          )}

          <div className="mt-6">
            <div className="hsc-label mb-2">Atau cari manual</div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
              <input className="hsc-input pl-9" placeholder="Cari nama member" value={q} onChange={(e) => setQ(e.target.value)} data-testid="scan-member-search" />
            </div>
            {filtered.map((m) => (
              <button key={m.id} onClick={() => { setQ(""); resolve(memberQrValue(m)); }}
                className="mt-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#141416]" data-testid="scan-member-option">
                <span className="text-white">{m.name}</span><TierBadge tier={m.tier} />
              </button>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-4 hsc-label">Hasil</div>
          {loading ? <Loading /> : !result ? (
            <EmptyState icon={User} title="Belum ada member dipindai" subtitle="Scan QR atau cari manual untuk melihat identitas member." />
          ) : (
            <QuickView result={result} onDetail={() => navigate(`${base}/members/${result.member.id}`)}
              onRecord={() => navigate(`${base}/record`)} />
          )}
        </Card>
      </div>
    </div>
  );
}

function QuickView({ result, onDetail, onRecord }) {
  const { member, membership_summary: s } = result;
  const color = s.status === "ACTIVE" ? "#7CD992" : s.status === "EXPIRED" ? "#FF8080" : "#8E8E93";
  return (
    <div className="space-y-5" data-testid="scan-quickview">
      <div className="rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-xl text-white" data-testid="scan-member-name">{member.name}</div>
          <TierBadge tier={member.tier} />
        </div>
        <div className="mt-1 text-xs text-[#8E8E93]">{s.categories.length ? s.categories.join(" · ") + " Membership" : "Non-Membership"}</div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border border-[#1F1F22] p-3">
          <div className="hsc-label">Status</div>
          <div className="mt-1 font-bold uppercase" style={{ color }} data-testid="scan-status">{s.status}</div>
        </div>
        <div className="rounded-lg border border-[#1F1F22] p-3">
          <div className="hsc-label">{s.status === "EXPIRED" ? "Expired" : "Valid Until"}</div>
          <div className="mt-1 text-white">{s.valid_until ? formatDate(s.valid_until) : "—"}</div>
        </div>
        <div className="rounded-lg border border-[#1F1F22] p-3">
          <div className="hsc-label">Poin</div>
          <div className="mt-1 text-[#E7C25B]">{member.points}</div>
        </div>
        <div className="rounded-lg border border-[#1F1F22] p-3">
          <div className="hsc-label">Kunjungan</div>
          <div className="mt-1 text-white">{member.visits || 0}</div>
        </div>
      </div>

      <div>
        <div className="hsc-label mb-1">Member ID</div>
        <div className="break-all font-mono text-[12px] text-[#A1A1A6]">{String(member.id).toUpperCase()}</div>
      </div>

      {result.memberships?.length > 0 && (
        <div className="space-y-2">
          {result.memberships.map((m, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-[#1F1F22] px-3 py-2 text-sm">
              <span className="text-white">{m.category}</span>
              <div className="flex items-center gap-2"><SeverityTag value={m.status} /><span className="text-[11px] text-[#6E6E73]">{m.days_left}h</span></div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onDetail} data-testid="scan-view-detail"><ArrowRight className="h-4 w-4" /> Lihat Detail</Button>
        <Button variant="outline" onClick={onRecord} data-testid="scan-record-txn"><PlusCircle className="h-4 w-4" /> Catat Transaksi</Button>
      </div>
    </div>
  );
}
